import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../../../style/reports.css";

import EyeIcon from "../../../assets/icons/EyeIcon.js";
import NotificationIcon from "../../../assets/icons/NotificationIcon.js";
import NotifyIcon from "../../../assets/icons/NotifyIcon.js";

import FilterIcon from "../../../assets/icons/FilterIcon.js";
import NoDataIcon from "../../../assets/icons/NoDataIcon.js";
import ArrowDownUpIcon from "../../../assets/icons/ArrowDownUpIcon.js";

import TotalCerdit from "../../../assets/total-cerdit.svg";
import TotalDebit from "../../../assets/total-debit.svg";
import BalanceIcon from "../../../assets/balance-icon.svg";
import StockValueIcon from "../../../assets/stock-value.svg";
import TotalCerditIcon from "../../../assets/total-credit-icon.svg";
import TotalDebitIcon from "../../../assets/total-debit-icon.svg";

import {
  ChevronDown,
  Search,
  Check,
  Download,
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import FilePdf from "../../../assets/file-pdf.svg";
import FileExcel from "../../../assets/microsoft-excel-logo.svg";
import FileCsv from "../../../assets/file-csv.svg";

import { getCustomers, getOverdueCustomers } from "../../../services/customers-api";
import { financeJournalAPI } from "../../../services";

function Reports() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [balanceFilter, setBalanceFilter] = useState("all");
  const filterDropdownRef = useRef(null);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportDropdownRef = useRef(null);
  const [transactionsList, setTransactionsList] = useState([]);
  const [activeTab, setActiveTab] = useState("customers-overview");
  const [activeNotifications, setActiveNotifications] = useState(new Set());
  const [isNotifyAllActive, setIsNotifyAllActive] = useState(false);

  // API state management
  const [customers, setCustomers] = useState([]);
  const [dueCustomers, setDueCustomers] = useState([]);
  const [, setLoading] = useState(false);
  const [, setError] = useState(null);
  const [dueCustomersCount, setDueCustomersCount] = useState(0);

  // Finance report metrics state
  const [reportMetrics, setReportMetrics] = useState({
    totalCredit: 0,
    totalDebit: 0,
    balance: 0,
    yesterdayCredit: 0,
    yesterdayDebit: 0,
    creditChange: 0,
    debitChange: 0,
    stockValue: 0,
    availableStocks: 0,
    stockChange: 0,
    creditUserCount: 0,
    debitUserCount: 0,
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const itemsPerPage = 25;

  // Refs to prevent duplicate API calls
  const isFirstLoad = useRef(true);
  const reportMetricsFetching = useRef(false);
  const customersFetching = useRef(false);
  const dueCustomersFetching = useRef(false);

  // Fetch finance report metrics from API
  const fetchReportMetrics = async () => {
    // Prevent duplicate simultaneous calls
    if (reportMetricsFetching.current) {
      return;
    }
    reportMetricsFetching.current = true;
    try {
      const response = await financeJournalAPI.getFinanceReportMetrics();

      if (response && response.data) {
        const data = response.data;
        setReportMetrics({
          totalCredit: data.total_credit?.total_amount || 0,
          totalDebit: data.total_debit?.total_amount || 0,
          balance: data.balance?.amount || 0,
          yesterdayCredit: data.total_credit?.previous_amount || 0,
          yesterdayDebit: data.total_debit?.previous_amount || 0,
          creditChange: data.total_credit?.percentage || 0,
          debitChange: data.total_debit?.percentage || 0,
          stockValue: data.stock_value?.amount || 0,
          availableStocks: data.stock_value?.available_stocks || 0,
          stockChange: data.stock_value?.percentage || 0,
          creditUserCount: data.total_credit?.user_count || 0,
          debitUserCount: data.total_debit?.user_count || 0,
        });
      }
    } catch (error) {
      console.error("Failed to fetch report metrics:", error);
    } finally {
      reportMetricsFetching.current = false;
    }
  };

  // API functions
  const fetchCustomers = async (searchTerm = "", filter = "", page = 1) => {
    // Prevent duplicate simultaneous calls
    if (customersFetching.current) {
      return;
    }
    customersFetching.current = true;
    setLoading(true);
    setError(null);
    try {
      // For customers overview: use regular API with filters parameter
      const { response, error } = await getCustomers(
        searchTerm,
        page,
        itemsPerPage,
        filter
      );
      if (error) {
        setError(error.message);
        console.error("Error fetching customers:", error);
      } else {
        // Handle the API response structure: { status, message, data: [...] }
        const customersData = response?.data || response?.results || [];
        setCustomers(customersData);

        // Calculate pagination info
        // If API provides total, use it; otherwise estimate based on results
        const total = response?.total || 
          (customersData.length === itemsPerPage ? (page * itemsPerPage) + 1 : page * itemsPerPage - (itemsPerPage - customersData.length));
        setTotalCustomers(total);
        setTotalPages(response?.total ? Math.ceil(total / itemsPerPage) : (customersData.length === itemsPerPage ? page + 1 : page));
        setCurrentPage(page);
      }
    } catch (err) {
      setError("Failed to fetch customers");
      console.error("Error fetching customers:", err);
    } finally {
      setLoading(false);
      customersFetching.current = false;
    }
  };

  const fetchDueCustomers = async (searchTerm = "", page = 1) => {
    // Prevent duplicate simultaneous calls
    if (dueCustomersFetching.current) {
      return;
    }
    dueCustomersFetching.current = true;
    setLoading(true);
    setError(null);
    try {
      // For due customers: use API with screen_type=overdue parameter
      const { response, error } = await getOverdueCustomers(
        searchTerm,
        page,
        itemsPerPage
      );
      if (error) {
        setError(error.message);
        console.error("Error fetching due customers:", error);
      } else {
        // Handle the API response structure: { status, message, data: [...] }
        const dueCustomersData = response?.data || response?.results || [];
        setDueCustomers(dueCustomersData);
        setDueCustomersCount(dueCustomersData.length);

        // Calculate pagination info for due customers
        // If API provides total, use it; otherwise estimate based on results
        const total = response?.total || 
          (dueCustomersData.length === itemsPerPage ? (page * itemsPerPage) + 1 : page * itemsPerPage - (itemsPerPage - dueCustomersData.length));
        setTotalCustomers(total);
        setTotalPages(response?.total ? Math.ceil(total / itemsPerPage) : (dueCustomersData.length === itemsPerPage ? page + 1 : page));
        setCurrentPage(page);
      }
    } catch (err) {
      setError("Failed to fetch due customers");
      console.error("Error fetching due customers:", err);
    } finally {
      setLoading(false);
      dueCustomersFetching.current = false;
    }
  };

  // Initialize transactions with sample data
  React.useEffect(() => {
    if (transactionsList.length === 0) {
      setTransactionsList([
        {
          id: "TRN_250118",
          customer: "Hussain Mansuri",
          note: "Payment received for...",
          credit: 1870.0,
          debit: 0,
          balance: 1180.5,
          type: "sale",
        },
        {
          id: "TRN_250119",
          customer: "Himesh Karim",
          note: "Refund processed for...",
          credit: 0,
          debit: 560.0,
          balance: 20180.5,
          type: "credit",
        },
        {
          id: "TRN_250120",
          customer: "Himesh Karim",
          note: "Refund processed for...",
          credit: 0,
          debit: 900.0,
          balance: -660.5,
          type: "debit",
        },
      ]);
    }
  }, []);

  // Load customers and report metrics on component mount (only once)
  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      fetchCustomers("", "", 1);
      fetchReportMetrics();
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isCustomerDropdownOpen &&
        !event.target.closest(".journal-customer-dropdown-container")
      ) {
        setIsCustomerDropdownOpen(false);
      }
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
      if (
        exportDropdownRef.current &&
        !exportDropdownRef.current.contains(event.target)
      ) {
        setShowExportDropdown(false);
      }
    };

    if (
      isCustomerDropdownOpen ||
      showDatePicker ||
      showFilterDropdown ||
      showExportDropdown
    ) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    isCustomerDropdownOpen,
    showDatePicker,
    showFilterDropdown,
    showExportDropdown,
  ]);

  // Filter options and functions
  const filterOptions = [
    { value: "all", label: "All" },
    { value: "credit", label: "Credit" },
    { value: "debit", label: "Debit" },
  ];

  const handleFilterSelect = (value) => {
    setBalanceFilter(value);
    setShowFilterDropdown(false);
  };

  const getFilterLabel = () => {
    const option = filterOptions.find((opt) => opt.value === balanceFilter);
    return option ? option.label : "All";
  };

  // Handle search with debouncing (skip on first load)
  useEffect(() => {
    if (isFirstLoad.current) {
      return;
    }
    
    const timeoutId = setTimeout(() => {
      if (activeTab === "customers-overview") {
        // For customers overview: use regular API with search and filter
        const filterValue = balanceFilter === "all" ? "" : balanceFilter;
        fetchCustomers(searchTerm, filterValue, 1);
      } else if (activeTab === "due-customers") {
        // For due customers: use overdue API with search only
        fetchDueCustomers(searchTerm, 1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, balanceFilter]);

  // Separate effect for tab switching (only fetch if data is empty and not already fetching)
  useEffect(() => {
    if (isFirstLoad.current) {
      return; // Skip on initial mount, already handled in initial useEffect
    }
    
    if (activeTab === "due-customers" && dueCustomers.length === 0 && !dueCustomersFetching.current) {
      fetchDueCustomers("", 1);
    } else if (activeTab === "customers-overview" && customers.length === 0 && !customersFetching.current) {
      const filterValue = balanceFilter === "all" ? "" : balanceFilter;
      fetchCustomers("", filterValue, 1);
    }
  }, [activeTab]);

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      if (activeTab === "customers-overview") {
        // For customers overview: use regular API with search and filter
        const filterValue = balanceFilter === "all" ? "" : balanceFilter;
        fetchCustomers(searchTerm, filterValue, newPage);
      } else if (activeTab === "due-customers") {
        // For due customers: use overdue API with search only
        fetchDueCustomers(searchTerm, newPage);
      }
    }
  };

  // Filter and search functionality
  const getFilteredCustomers = () => {
    // Since API already handles filtering, just return the data directly
    const filtered = activeTab === "due-customers" ? dueCustomers : customers;
    return filtered;
  };

  const filteredCustomers = getFilteredCustomers();

  // Use API metrics instead of calculated totals with proper type conversion
  const {
    totalCredit = 0,
    totalDebit = 0,
    balance: netBalance = 0,
  } = reportMetrics;

  // Ensure all values are numbers
  const safeTotalCredit =
    typeof totalCredit === "number"
      ? totalCredit
      : parseFloat(totalCredit) || 0;
  const safeTotalDebit =
    typeof totalDebit === "number" ? totalDebit : parseFloat(totalDebit) || 0;
  const safeNetBalance =
    typeof netBalance === "number" ? netBalance : parseFloat(netBalance) || 0;
  

  // Export options and functions
  const exportOptions = [
    { value: "pdf", label: "PDF", icon: FilePdf },
    { value: "excel", label: "Excel", icon: FileExcel },
    { value: "csv", label: "CSV", icon: FileCsv },
  ];

  const handleExportSelect = (value) => {
    setShowExportDropdown(false);

    switch (value) {
      case "pdf":
        // For both tabs, navigate to print page
        if (activeTab === "due-customers") {
          navigate("/finance/reports/due-customers/print", {
            state: { customers: filteredCustomers },
          });
        } else if (activeTab === "customers-overview") {
          navigate("/finance/reports/customers-overview/print", {
            state: { customers: filteredCustomers },
          });
        } else {
          // Fallback to direct export
          exportToPDF();
        }
        break;
      case "excel":
        exportToExcel();
        break;
      case "csv":
        exportToCSV();
        break;
      default:
        console.log(`Export format ${value} not supported`);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(16);
    doc.text("Customers Report", 14, 22);

    // Add date
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

    // Prepare table data
    const tableData = filteredCustomers.map((customer) => [
      customer.name,
      customer.user_email,
      customer.mobile,
      customer.address,
      `€${parseFloat(customer.balance || 0).toFixed(2)}`,
    ]);

    // Create table using the imported autoTable function
    autoTable(doc, {
      head: [["Name", "Email", "Phone", "Address", "Balance"]],
      body: tableData,
      startY: 40,
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [246, 248, 250],
        textColor: [87, 96, 106],
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
      columnStyles: {
        4: { halign: "right" }, // Balance column right-aligned
      },
    });

    // Save the PDF
    doc.save("customers-report.pdf");
  };

  const exportToExcel = () => {
    // Prepare data for Excel
    const excelData = filteredCustomers.map((customer) => ({
      Name: customer.name,
      Email: customer.user_email,
      Phone: customer.mobile,
      Address: customer.address,
      Balance: parseFloat(customer.balance || 0),
    }));

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Customers");

    // Set column widths
    ws["!cols"] = [
      { wch: 20 }, // Name
      { wch: 25 }, // Email
      { wch: 15 }, // Phone
      { wch: 35 }, // Address
      { wch: 12 }, // Balance
    ];

    // Save the Excel file
    XLSX.writeFile(wb, "customers-report.xlsx");
  };

  const exportToCSV = () => {
    // Prepare CSV data
    const csvHeaders = ["Name", "Email", "Phone", "Address", "Balance"];
    const csvData = filteredCustomers.map((customer) => [
      customer.name,
      customer.user_email,
      customer.mobile,
      customer.address,
      parseFloat(customer.balance || 0),
    ]);

    // Combine headers and data
    const csvContent = [csvHeaders, ...csvData]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "customers-report.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle notification click
  const handleNotificationClick = (customerId) => {
    const newActiveNotifications = new Set(activeNotifications);

    if (newActiveNotifications.has(customerId)) {
      newActiveNotifications.delete(customerId);
    } else {
      newActiveNotifications.add(customerId);
    }

    setActiveNotifications(newActiveNotifications);

    // Check if all notifications are active to update Notify All state
    const allCustomerIds = filteredCustomers.map((customer) => customer.id);
    const allActive = allCustomerIds.every((id) =>
      newActiveNotifications.has(id)
    );
    setIsNotifyAllActive(allActive);

  };

  // Handle notify all click
  const handleNotifyAllClick = () => {
    const allCustomerIds = filteredCustomers.map((customer) => customer.id);

    if (isNotifyAllActive) {
      // Deactivate all notifications
      setActiveNotifications(new Set());
      setIsNotifyAllActive(false);
    } else {
      // Activate all notifications
      setActiveNotifications(new Set(allCustomerIds));
      setIsNotifyAllActive(true);
    }
  };

  return (
    <>
      <div className="report-container">
        {/* Transaction Form */}
        <div className="report-transaction-form">
          {/* Header */}
          <div className="report-header">
            <div className="report-title-section">
              <h1 className="report-title">Finance Reports</h1>
              <p className="report-subtitle">
                Manage transactions and track financial data
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="row mb-4">
            <div className="col-md-3">
              <div
                className="p-3 d-flex align-items-start justify-content-between daily-report-card-shadow"
                style={{
                  border: "1px solid #EAEEF2",
                  borderRadius: "10px",
                  height: "167px",
                }}
              >
                <div className="d-flex flex-column align-items-start justify-content-center gap-2">
                  <div>
                    <small className="report-DailyReportView-text">
                      Total Credit
                    </small>
                    <h5 className="report-DailyReportView-value">
                      €{safeTotalCredit.toFixed(2)}
                    </h5>
                  </div>

                  <div className="d-flex flex-row gap-1">
                    <img
                      src={TotalCerditIcon}
                      alt="Total Credit"
                      className="TotalCerditIcon"
                    />
                    <h5 className="report-DailyReportView-SaleIcon-value">
                      {reportMetrics.creditUserCount}
                    </h5>
                  </div>

                  {/* <div
                    className="d-flex flex-row align-items-center gap-1"
                    style={{ width: "120%" }}
                  >
                    <div className={`d-flex flex-row gap-1 align-items-center ${
                      safeCreditChange >= 0 
                        ? "report-DailyReportView-Sale-badge-green" 
                        : "report-DailyReportView-Sale-badge-red"
                    }`}>
                      {safeCreditChange >= 0 ? <TrendUp /> : <TrendDown />}
                      <h5 className="mt-2">
                        {safeCreditChange >= 0 ? "+" : ""}{safeCreditChange.toFixed(2)}%
                      </h5>
                    </div>
                    <h5 className="report-DailyReportView-SaleIcon-text mt-1">
                      From last month
                    </h5>
                  </div> */}
                </div>

                <img src={TotalCerdit} alt="Total Credit" />
              </div>
            </div>

            <div className="col-md-3">
              <div
                className="p-3 d-flex align-items-start justify-content-between daily-report-card-shadow"
                style={{
                  border: "1px solid #EAEEF2",
                  borderRadius: "10px",
                  height: "167px",
                }}
              >
                <div className="d-flex flex-column align-items-start justify-content-center gap-2">
                  <div>
                    <small className="report-DailyReportView-text">
                      Total Debit
                    </small>
                    <h5 className="report-DailyReportView-value">
                      €{safeTotalDebit.toFixed(2)}
                    </h5>
                  </div>

                  <div className="d-flex flex-row gap-1">
                    <img
                      src={TotalDebitIcon}
                      alt="Total Debit"
                      className="TotalCerditIcon"
                    />
                    <h5 className="report-DailyReportView-SaleIcon-value">
                      {reportMetrics.debitUserCount}
                    </h5>
                  </div>

                  {/* <div
                    className="d-flex flex-row align-items-center gap-1"
                    style={{ width: "120%" }}
                  >
                    <div className={`d-flex flex-row gap-1 align-items-center ${
                      safeDebitChange >= 0 
                        ? "report-DailyReportView-Sale-badge-green" 
                        : "report-DailyReportView-Sale-badge-red"
                    }`}>
                      {safeDebitChange >= 0 ? <TrendUp /> : <TrendDown />}
                      <h5 className="mt-2">
                        {safeDebitChange >= 0 ? "+" : ""}{safeDebitChange.toFixed(2)}%
                      </h5>
                    </div>
                    <h5 className="report-DailyReportView-SaleIcon-text mt-1">
                      From last month
                    </h5>
                  </div> */}
                </div>

                <img src={TotalDebit} alt="Total Debit" />
              </div>
            </div>

            <div className="col-md-3">
              <div
                className="p-3 d-flex align-items-start justify-content-between daily-report-card-shadow"
                style={{
                  border: "1px solid #EAEEF2",
                  borderRadius: "10px",
                  height: "167px",
                }}
              >
                <div className="d-flex flex-column align-items-start justify-content-center gap-4">
                  <div>
                    <small className="report-DailyReportView-text">
                      Balance
                    </small>
                    <h5 className="report-DailyReportView-value">
                      €{safeNetBalance.toFixed(2)}
                    </h5>
                  </div>

                  {/* <div
                    className="d-flex flex-row align-items-center gap-1"
                    style={{ width: "120%", marginTop: "1rem" }}
                  >
                    <div className={`d-flex flex-row gap-1 align-items-center ${
                      safeNetBalance >= 0 
                        ? "report-DailyReportView-Sale-badge-green" 
                        : "report-DailyReportView-Sale-badge-red"
                    }`}>
                      {safeNetBalance >= 0 ? <TrendUp /> : <TrendDown />}
                      <h5 className="mt-2">
                        {safeNetBalance >= 0 ? "+" : ""}{safeNetBalance.toFixed(2)}%
                      </h5>
                    </div>
                    <h5 className="report-DailyReportView-SaleIcon-text mt-1">
                      From last month
                    </h5>
                  </div> */}
                </div>

                <img src={BalanceIcon} alt="Total Credit" />
              </div>
            </div>

            <div className="col-md-3">
              <div
                className="p-3 d-flex align-items-start justify-content-between daily-report-card-shadow"
                style={{
                  border: "1px solid #EAEEF2",
                  borderRadius: "10px",
                  height: "167px",
                }}
              >
                <div className="d-flex flex-column align-items-start justify-between">
                  <div>
                    <small className="report-DailyReportView-text">
                      Stock Value
                    </small>
                    <h5 className="report-DailyReportView-value">
                      €
                      {typeof reportMetrics.stockValue === "number"
                        ? reportMetrics.stockValue.toFixed(2)
                        : (parseFloat(reportMetrics.stockValue) || 0).toFixed(
                            2
                          )}
                    </h5>
                  </div>

                  <div className="available-stocks">
                    <p
                      style={{
                        fontSize: "12px",
                        fontWeight: "400",
                        color: "#6E7781",
                      }}
                    >
                      Available Stocks
                    </p>
                    <p
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#424A53",
                        marginTop: "-1rem",
                      }}
                    >
                      {typeof reportMetrics.availableStocks === "number"
                        ? reportMetrics.availableStocks
                        : parseFloat(reportMetrics.availableStocks) || 0}
                    </p>
                  </div>
                </div>

                <img src={StockValueIcon} alt="Total Balance" />
              </div>
            </div>
          </div>

          {/* Two Tabs (Customers Overview) and (Due Customers) */}
          <div className="report-tabs-container">
            <div className="report-tabs">
              <a
                href="/finance/reports/customers-overview"
                className={`report-tab ${
                  activeTab === "customers-overview" ? "active" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("customers-overview");
                }}
              >
                Customers Overview
              </a>
              <a
                href="/finance/reports/due-customers"
                className={`report-tab ${
                  activeTab === "due-customers" ? "active" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("due-customers");
                }}
              >
                Due Customers
              </a>
              {/* badge for "due-customers" */}
              <div className="report-due-customers-badge">
                {dueCustomersCount}
              </div>
            </div>
          </div>

          {/* Conditional Content Based on Active Tab */}
          {activeTab === "customers-overview" && (
            <>
              {/* Search and Filter */}
              <div className="report-transactions-header">
                <div className="report-transactions-controls">
                  <div className="report-search-wrapper">
                    <Search className="report-search-icon" size={16} />
                    <input
                      type="text"
                      className="report-transactions-search"
                      placeholder="Search by customer name or amount..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <div
                    className="report-filter-dropdown"
                    ref={filterDropdownRef}
                  >
                    <button
                      className="report-filter-button"
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <FilterIcon size={16} />
                        {getFilterLabel()}
                      </div>

                      <ChevronDown size={16} />
                    </button>

                    <div
                      className={`report-filter-dropdown-content ${
                        showFilterDropdown ? "show" : ""
                      }`}
                    >
                      {filterOptions.map((option) => (
                        <div
                          key={option.value}
                          className={`report-filter-dropdown-item ${
                            balanceFilter === option.value ? "selected" : ""
                          }`}
                          onClick={() => handleFilterSelect(option.value)}
                        >
                          <Check
                            size={16}
                            className="report-filter-dropdown-check"
                          />
                          {option.label}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Export Dropdown */}
                  <div
                    className="report-export-dropdown"
                    ref={exportDropdownRef}
                  >
                    <button
                      className="report-export-button"
                      onClick={() => setShowExportDropdown(!showExportDropdown)}
                    >
                      <Download size={16} />
                      Export
                      <ChevronDown
                        size={12}
                        className={`report-export-dropdown-arrow ${
                          showExportDropdown ? "rotated" : ""
                        }`}
                      />
                    </button>

                    <div
                      className={`report-export-dropdown-content ${
                        showExportDropdown ? "show" : ""
                      }`}
                    >
                      {exportOptions.map((option) => (
                        <div
                          key={option.value}
                          className="report-export-dropdown-item"
                          onClick={() => handleExportSelect(option.value)}
                        >
                          <img
                            src={option.icon}
                            alt={option.label}
                            width="16"
                            height="16"
                          />
                          {option.label}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Finance History Table */}
              <div className="report-table-container">
                <div className="report-table-responsive">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>NAME</th>
                        <th>EMAIL</th>
                        <th>PHONE</th>
                        <th>ADDRESS</th>
                        <th>
                          BALANCE <ArrowDownUpIcon />
                        </th>
                        <th style={{ textAlign: "end", paddingRight: "30px" }}>
                          ACTION
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCustomers.length === 0 ? (
                        <tr>
                          <td
                            colSpan="6"
                            className="report-no-products-message"
                          >
                            <div
                              className=""
                              style={{
                                marginBottom: "4rem",
                                marginTop: "4rem",
                              }}
                            >
                              <NoDataIcon />
                              <h3 style={{ marginTop: "2rem" }}>
                                No customers found
                              </h3>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredCustomers.map((customer, index) => (
                          <tr key={customer.id}>
                            <td>{customer.name}</td>
                            <td>{customer.user_email}</td>
                            <td>{customer.mobile}</td>
                            <td>{customer.address}</td>
                            <td>
                              <div
                                className={`balance-badge ${
                                  parseFloat(customer.balance || 0) < 0
                                    ? "negative"
                                    : "positive"
                                }`}
                              >
                                €{parseFloat(customer.balance || 0).toFixed(2)}
                              </div>
                            </td>
                            <td>
                              <div className="report-action-buttons">
                                <button
                                  className="report-action-btn report-view"
                                  title="View customer"
                                  onClick={() => navigate(`/customer/${customer.id}`)}
                                >
                                  <EyeIcon />
                                </button>
                                <button
                                  className={`report-action-btn report-notification ${
                                    activeNotifications.has(customer.id)
                                      ? "active"
                                      : ""
                                  }`}
                                  title="Send notification"
                                  onClick={() =>
                                    handleNotificationClick(customer.id)
                                  }
                                >
                                  <NotificationIcon />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-wrapper">
                  <span className="pagination-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      Previous
                    </button>
                    <button
                      className="pagination-btn-next"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Due Customers Tab Content */}
          {activeTab === "due-customers" && (
            <>
              {/* Search and Filter */}
              <div className="report-transactions-header">
                <div className="report-transactions-controls">
                  <div className="report-search-wrapper">
                    <Search className="report-search-icon" size={16} />
                    <input
                      type="text"
                      className="report-transactions-search"
                      placeholder="Search by customer name or amount..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* Export Dropdown */}
                  <div
                    className="report-export-dropdown"
                    ref={exportDropdownRef}
                  >
                    <button
                      className="report-export-button"
                      onClick={() => setShowExportDropdown(!showExportDropdown)}
                    >
                      <Download size={16} />
                      Export
                      <ChevronDown
                        size={12}
                        className={`report-export-dropdown-arrow ${
                          showExportDropdown ? "rotated" : ""
                        }`}
                      />
                    </button>

                    <div
                      className={`report-export-dropdown-content ${
                        showExportDropdown ? "show" : ""
                      }`}
                    >
                      {exportOptions.map((option) => (
                        <div
                          key={option.value}
                          className="report-export-dropdown-item"
                          onClick={() => handleExportSelect(option.value)}
                        >
                          <img
                            src={option.icon}
                            alt={option.label}
                            width="16"
                            height="16"
                          />
                          {option.label}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Notify All Button */}
                  <div>
                    <button
                      className={`report-notify-all-btn ${
                        isNotifyAllActive ? "active" : ""
                      }`}
                      title={
                        isNotifyAllActive
                          ? "Deactivate all notifications"
                          : "Activate all notifications"
                      }
                      onClick={handleNotifyAllClick}
                    >
                      <NotifyIcon />
                      Notify All
                    </button>
                  </div>
                </div>
              </div>

              {/* Finance History Table */}
              <div className="report-table-container">
                <div className="report-table-responsive">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>NAME</th>
                        <th>EMAIL</th>
                        <th>PHONE</th>
                        <th>ADDRESS</th>
                        <th>
                          BALANCE <ArrowDownUpIcon />
                        </th>
                        <th style={{ textAlign: "end", paddingRight: "30px" }}>
                          ACTION
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCustomers.length === 0 ? (
                        <tr>
                          <td
                            colSpan="6"
                            className="report-no-products-message"
                          >
                            <div
                              className=""
                              style={{
                                marginBottom: "4rem",
                                marginTop: "4rem",
                              }}
                            >
                              <NoDataIcon />
                              <h3 style={{ marginTop: "2rem" }}>
                                No customers found
                              </h3>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredCustomers.map((customer, index) => (
                          <tr key={customer.id}>
                            <td>{customer.name}</td>
                            <td>{customer.user_email}</td>
                            <td>{customer.mobile}</td>
                            <td>{customer.address}</td>
                            <td>
                              <div
                                className={`balance-badge ${
                                  parseFloat(customer.balance || 0) < 0
                                    ? "negative"
                                    : "positive"
                                }`}
                              >
                                €{parseFloat(customer.balance || 0).toFixed(2)}
                              </div>
                            </td>
                            <td>
                              <div className="report-action-buttons">
                                <button
                                  className="report-action-btn report-view"
                                  title="View customer"
                                  onClick={() => navigate(`/customer/${customer.id}`)}
                                >
                                  <EyeIcon />
                                </button>
                                <button
                                  className={`report-action-btn report-notification ${
                                    activeNotifications.has(customer.id)
                                      ? "active"
                                      : ""
                                  }`}
                                  title="Send notification"
                                  onClick={() =>
                                    handleNotificationClick(customer.id)
                                  }
                                >
                                  <NotificationIcon />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-wrapper">
                  <span className="pagination-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      Previous
                    </button>
                    <button
                      className="pagination-btn-next"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default Reports;
